import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

function Inbound_Ip() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    grp: 1,
    ip: '',
    mask: 32,
    port: 0,
    proto: 'any',
    pattern: '',
    context_info: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

  // Protocol options
  const protocolOptions = [
    { value: "any", label: "Any" },
    { value: "tcp", label: "TCP" },
    { value: "udp", label: "UDP" }
  ];

  // Fetch all inbound IP addresses
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('superadminToken');
      if (!token) {
        navigate('/superadmin/login');
        return;
      }

      const response = await axios.get(`${API_URL}/inbound-ip`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setAddresses(response.data.data);
        setError(null);
      } else {
        throw new Error(response.data.error || 'Failed to fetch inbound IP addresses');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('superadminToken');
        localStorage.removeItem('superadminUser');
        navigate('/superadmin/login');
        return;
      }
      setError('Failed to fetch inbound IP addresses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superadminToken');
      if (!token) {
        navigate('/superadmin/login');
        return;
      }

      if (editMode) {
        await axios.put(`${API_URL}/inbound-ip/${currentId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setSuccessMessage('IP address updated successfully');
      } else {
        await axios.post(`${API_URL}/inbound-ip`, formData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setSuccessMessage('IP address added successfully');
      }
      fetchAddresses();
      resetForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('superadminToken');
        localStorage.removeItem('superadminUser');
        navigate('/superadmin/login');
        return;
      }
      setError(editMode ? 'Failed to update IP address' : 'Failed to add IP address');
      console.error(err);
      // Show error in modal and clear after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      grp: address.grp,
      ip: address.ip,
      mask: address.mask,
      port: address.port,
      proto: address.proto,
      pattern: address.pattern || '',
      context_info: address.context_info || ''
    });
    setCurrentId(address.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this IP address?')) {
      try {
        const token = localStorage.getItem('superadminToken');
        if (!token) {
          navigate('/superadmin/login');
          return;
        }

        await axios.delete(`${API_URL}/inbound-ip/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        fetchAddresses();
        setSuccessMessage('IP address deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('superadminToken');
          localStorage.removeItem('superadminUser');
          navigate('/superadmin/login');
          return;
        }
        setError('Failed to delete IP address');
        console.error(err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      grp: '',
      ip: '',
      mask: '',
      port: '',
      proto: '',
      pattern: '',
      context_info: ''
    });
    setEditMode(false);
    setCurrentId(null);
    setShowModal(false);
  };

  // Filter addresses based on search term
  const filteredAddresses = addresses.filter(address => 
    address.ip.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (address.pattern && address.pattern.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (address.context_info && address.context_info.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            {successMessage && (
              <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
                {successMessage}
              </div>
            )}

            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Inbound IP Management
                </h1>
              </div>

              <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:space-x-4">
                <input
                  type="text"
                  placeholder="Search IP addresses..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
                <button
                  onClick={() => {
                    setEditMode(false);
                    resetForm();
                    setShowModal(true);
                  }}
                  className="mt-4 sm:mt-0 px-4 py-2 whitespace-nowrap bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Add IP Address
                </button>
              </div>
            </div>

            {error && !showModal && (
              <div className="mt-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-900">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Group
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          IP Address
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Mask
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Port
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Protocol
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Pattern
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Context Info
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-800">
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="py-10 text-center">
                            <div className="flex justify-center items-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                            </div>
                          </td>
                        </tr>
                      ) : filteredAddresses.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="py-4 text-center text-sm text-gray-500">
                            No IP addresses found
                          </td>
                        </tr>
                      ) : (
                        filteredAddresses.map((address) => (
                          <tr key={address.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.grp}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.ip}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.mask}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.port}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.proto}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.pattern || '-'}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {address.context_info || '-'}
                            </td>
                            <td className="py-4 flex space-x-2 items-center justify-center">
                              {/* Edit Button */}
                              <button
                                onClick={() => handleEdit(address)}
                                className="text-blue-400 hover:text-blue-500"
                                title="Edit"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              {/* Delete Button */}
                              <button
                                onClick={() => handleDelete(address.id)}
                                className="text-red-400 hover:text-red-500"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editMode ? "Edit IP Address" : "Add New IP Address"}
            </h2>

            {/* Add error message display */}
            {error && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-1">
                {/* Group Field */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Group
                  </label>
                  <input
                    type="number"
                    name="grp"
                    value={formData.grp}
                    onChange={handleInputChange}
                    placeholder="Enter Group"
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                {/* IP Address Field */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    IP Address
                  </label>
                  <input
                    type="text"
                    name="ip"
                    required
                    value={formData.ip}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Enter IP address"
                  />
                </div>

                {/* Mask Field */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Mask
                  </label>
                  <input
                    type="number"
                    name="mask"
                    min="0"
                    max="32"
                    value={formData.mask}
                    onChange={handleInputChange}
                    placeholder="Enter Mask"
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                {/* Port Field */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    name="port"
                    min="0"
                    max="65535"
                    value={formData.port}
                    onChange={handleInputChange}
                    placeholder="Enter Port"
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                {/* Protocol Field */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Protocol
                  </label>
                  <select
                    name="proto"
                    value={formData.proto}
                    onChange={handleInputChange}
                    placeholder="Select Protocol"
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {protocolOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pattern Field - Full width */}
                <div className="mb-1 col-span-2">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Pattern
                  </label>
                  <input
                    type="text"
                    name="pattern"
                    value={formData.pattern}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Enter pattern (optional)"
                  />
                </div>

                {/* Context Info Field - Full width */}
                <div className="mb-1 col-span-2">
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Context Info
                  </label>
                  <input
                    type="text"
                    name="context_info"
                    value={formData.context_info}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Enter context info (optional)"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editMode ? "Save Changes" : "Add IP Address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inbound_Ip;